/**
 * Utility functions for the generic AttributeMapping component
 */

import { VALIDATION_MESSAGES } from '../constants/attributeMappingConstants';
import {
  type GenericAttributeMapping,
  type ValidationResult,
} from '../data-layer/types';

/**
 * Get available Saviynt fields that are not currently used
 */
export const getAvailableSaviyntFields = (
  mappings: GenericAttributeMapping[],
  allSaviyntFields: string[]
): string[] => {
  const usedFields = new Set(
    mappings
      .map((m) => m.saviyntField)
      .filter((field) => field && field.trim() !== '')
  );

  return allSaviyntFields.filter((field) => !usedFields.has(field));
};

/**
 * Validate external app fields for uniqueness
 */
export const validateExternalAppFields = (
  mappings: GenericAttributeMapping[]
): ValidationResult => {
  const externalFields = mappings
    .map((m) => m.externalAppField.trim())
    .filter((field) => field !== '');

  const duplicates = externalFields.filter(
    (field, index) => externalFields.indexOf(field) !== index
  );

  return {
    isValid: duplicates.length === 0,
    errors: duplicates.map(
      (field) => `${VALIDATION_MESSAGES.DUPLICATE_FIELD}: "${field}"`
    ),
  };
};

/**
 * Validate that all required fields are filled
 */
export const validateRequiredFields = (
  mappings: GenericAttributeMapping[]
): ValidationResult => {
  const errors: string[] = [];

  mappings.forEach((mapping, index) => {
    if (!mapping.saviyntField || mapping.saviyntField.trim() === '') {
      errors.push(
        `Row ${index + 1}: ${
          VALIDATION_MESSAGES.REQUIRED_FIELD
        } (Saviynt field)`
      );
    }

    if (!mapping.externalAppField || mapping.externalAppField.trim() === '') {
      errors.push(
        `Row ${index + 1}: ${
          VALIDATION_MESSAGES.REQUIRED_FIELD
        } (External app field)`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate field name format (basic validation)
 */
export const validateFieldNameFormat = (fieldName: string): boolean => {
  // Basic validation: no empty, no special characters except underscore and dot
  const validPattern = /^[a-zA-Z][a-zA-Z0-9._]*$/;

  return validPattern.test(fieldName);
};

/**
 * Get validation error for a specific field
 */
export const getFieldValidationError = (
  mappings: GenericAttributeMapping[],
  currentId: string,
  fieldName: string,
  fieldType: 'saviyntField' | 'externalAppField'
): string | null => {
  if (!fieldName || fieldName.trim() === '') {
    return VALIDATION_MESSAGES.REQUIRED_FIELD;
  }

  if (fieldType === 'externalAppField') {
    // Check for duplicates
    const duplicates = mappings.filter(
      (m) =>
        m.id !== currentId && m.externalAppField.trim() === fieldName.trim()
    );

    if (duplicates.length > 0) {
      return VALIDATION_MESSAGES.DUPLICATE_FIELD;
    }

    // Check field name format
    if (!validateFieldNameFormat(fieldName)) {
      return VALIDATION_MESSAGES.INVALID_FIELD_NAME;
    }
  }

  return null;
};

/**
 * Generate unique ID for new mapping
 */
export const generateMappingId = (): string =>
  `mapping-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Check if mappings have changed from initial state
 */
export const hasMappingsChanged = (
  currentMappings: GenericAttributeMapping[],
  initialMappings: GenericAttributeMapping[]
): boolean =>
  JSON.stringify(currentMappings) !== JSON.stringify(initialMappings);

/**
 * Transform mappings to a format suitable for API calls
 */
export const transformMappingsForAPI = (
  mappings: GenericAttributeMapping[]
): Record<string, string> =>
  mappings.reduce<Record<string, string>>((acc, mapping) => {
    if (mapping.saviyntField && mapping.externalAppField) {
      acc[mapping.saviyntField] = mapping.externalAppField;
    }

    return acc;
  }, {});

/**
 * Create mapping from API response format
 */
export const createMappingsFromAPI = (
  apiMappings: Record<string, string>
): GenericAttributeMapping[] =>
  Object.entries(apiMappings)
    .filter(([, externalField]) => externalField.trim() !== '')
    .map(([saviyntField, externalField], index) => ({
      id: `mapping-${index + 1}`,
      saviyntField,
      externalAppField: externalField,
      saviyntValue: '',
      externalAppValue: '',
    }));
